import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useUpsertVacancyMutation,
  useSetVacancyStatusMutation,
  useBulkPublishVacanciesMutation,
  VacancyInput,
} from 'core/graphql';
import { GlobalToastTypeNames, ToastTypes } from 'types';

import { useToasts } from '../Toast/useToasts';
import { careerErrorMessage } from './careerModel';

interface IVacancyMutationsHook {
  /** Create when `id` is omitted, update when it is given. */
  saveVacancy: (input: VacancyInput, id?: string) => Promise<boolean>;
  /** Move one vacancy along the DRAFT→…→ARCHIVED workflow. */
  setStatus: (id: string, status: string) => Promise<boolean>;
  /** Publish many drafts at once; resolves with the count actually flipped. */
  bulkPublish: (ids: string[]) => Promise<number | null>;
  pending: boolean;
}

/**
 * Spec 0018 increment B — vacancy-side mutations for the Вакансии tab.
 *
 * Every failure surfaces the mapped `customErrors` code as an error toast; the
 * caller owns success toasts where the phrasing depends on counts (bulk
 * publish). The envelope is checked before the catch so a backend validation
 * error (e.g. dedup collision) reads as a toast, not a thrown exception.
 */
export const useVacancyMutations = (
  onChanged?: () => void,
): IVacancyMutationsHook => {
  const { t } = useTranslation();
  const { addToast } = useToasts();
  const [saveRun] = useUpsertVacancyMutation();
  const [statusRun] = useSetVacancyStatusMutation();
  const [bulkRun] = useBulkPublishVacanciesMutation();
  const [pending, setPending] = useState(false);

  const toastError = useCallback(
    (title: string, text?: string) => {
      addToast({
        type: GlobalToastTypeNames.CareerCenter,
        data: { type: ToastTypes.error, title, text },
      });
    },
    [addToast],
  );

  const saveVacancy: IVacancyMutationsHook['saveVacancy'] = useCallback(
    async (input, id) => {
      setPending(true);
      try {
        const result = await saveRun({ variables: { id, input } });
        const payload = result.data?.upsertVacancy;
        if (payload?.success) {
          onChanged?.();
          return true;
        }
        toastError(
          t('pages.careerCenter.vacancies.toastSaveError'),
          careerErrorMessage(t, payload?.customErrors),
        );
        return false;
      } catch (e) {
        toastError(
          t('pages.careerCenter.vacancies.toastSaveError'),
          (e as Error).message,
        );
        return false;
      } finally {
        setPending(false);
      }
    },
    [saveRun, onChanged, toastError, t],
  );

  const setStatus: IVacancyMutationsHook['setStatus'] = useCallback(
    async (id, status) => {
      setPending(true);
      try {
        const result = await statusRun({ variables: { id, status } });
        const payload = result.data?.setVacancyStatus;
        if (payload?.success) {
          onChanged?.();
          return true;
        }
        toastError(
          t('pages.careerCenter.vacancies.toastStatusError'),
          careerErrorMessage(t, payload?.customErrors),
        );
        return false;
      } catch (e) {
        toastError(
          t('pages.careerCenter.vacancies.toastStatusError'),
          (e as Error).message,
        );
        return false;
      } finally {
        setPending(false);
      }
    },
    [statusRun, onChanged, toastError, t],
  );

  const bulkPublish: IVacancyMutationsHook['bulkPublish'] = useCallback(
    async (ids) => {
      if (!ids.length) return 0;
      setPending(true);
      try {
        const result = await bulkRun({ variables: { ids } });
        const payload = result.data?.bulkPublishVacancies;
        if (payload?.success) {
          onChanged?.();
          return payload.published ?? 0;
        }
        toastError(
          t('pages.careerCenter.vacancies.toastPublishError'),
          careerErrorMessage(t, payload?.customErrors),
        );
        return null;
      } catch (e) {
        toastError(
          t('pages.careerCenter.vacancies.toastPublishError'),
          (e as Error).message,
        );
        return null;
      } finally {
        setPending(false);
      }
    },
    [bulkRun, onChanged, toastError, t],
  );

  return { saveVacancy, setStatus, bulkPublish, pending };
};
